import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';

import styles from './styles.module.css';
import ArrowIcon from '@/assets/icons/arrow.svg?react';
import HomeIcon from '@/assets/icons/home.svg?react';
import { useNavigationBar } from '@/context/NavigationBarContext';
import {
  formatLocationTurbineBreadcrumbLabels,
  isLocationTurbineBreadcrumb
} from '@/utils/turbineNavigation';

function Breadcrumb() {
  const { breadcrumbArray } = useNavigationBar();
  const navigate = useNavigate();
  const isLocationTurbineFlow = isLocationTurbineBreadcrumb(breadcrumbArray);

  const formattedLocationTurbineLabels = useMemo(() => {
    if (!isLocationTurbineFlow) {
      return null;
    }

    return formatLocationTurbineBreadcrumbLabels({
      locationNameLabel: breadcrumbArray[1]?.label || '',
      turbineLabel: breadcrumbArray[2]?.label || ''
    });
  }, [breadcrumbArray, isLocationTurbineFlow]);

  const getDisplayLabel = (index: number, originalLabel: string): string => {
    if (!formattedLocationTurbineLabels) {
      return originalLabel;
    }

    if (index === 0) {
      return formattedLocationTurbineLabels.locationsLabel;
    }

    if (index === 1) {
      return formattedLocationTurbineLabels.locationNameLabel;
    }

    if (index === 2) {
      return formattedLocationTurbineLabels.turbineLabel;
    }

    return originalLabel;
  };

  const getItemTitle = (
    index: number,
    originalLabel: string
  ): string | undefined => {
    const displayLabel = getDisplayLabel(index, originalLabel);
    return displayLabel !== originalLabel ? originalLabel : undefined;
  };

  return (
    <div
      className={`${styles.breadcrumbContainer} ${isLocationTurbineFlow ? styles.breadcrumbContainerCompact : ''}`}
    >
      <HomeIcon className={styles.homeIcon} onClick={() => navigate('/')} />
      <ArrowIcon className={styles.arrowIcon} />
      <nav aria-label="Breadcrumb">
        <ol
          className={`${styles.breadcrumbList} ${isLocationTurbineFlow ? styles.breadcrumbListCompact : ''}`}
        >
          {breadcrumbArray.map((item, index) => {
            const isLastItem = index === breadcrumbArray.length - 1;
            const displayLabel = getDisplayLabel(index, item.label);

            return (
              <React.Fragment key={index}>
                <li
                  aria-current={isLastItem ? 'page' : undefined}
                  className={styles.breadcrumbItemWrapper}
                >
                  <span
                    className={`${item.path ? styles.breadcrumbItemClickable : styles.breadcrumbItem} ${
                      isLastItem ? styles.breadcrumbCurrent : ''
                    }`}
                    title={getItemTitle(index, item.label)}
                    onClick={() => item.path && navigate(item.path)}
                  >
                    {displayLabel}
                  </span>
                </li>
                {!isLastItem && <ArrowIcon className={styles.arrowIcon} />}
              </React.Fragment>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}

export default Breadcrumb;
